// PRODUCT INTERFACES

import { ReservedArea } from '../auto-generated-strapi-types/types/components/ReservedArea';
import { Pages, ProductAssetsType } from './enum';

export interface BaseProduct {
  sku: string;
  skuFather?: string;
  name?: string;
  nameFather?: string;
  type?: string;
  quantity?: number;
  defaultPrice?: number;
  ce?: string;
  outOfSerie?: string;
  qtaMin?: number;
  qtaMinUnit?: string;
  qtaMinConversion?: number;
  video?: string;
  technicalImages?: string[];
  altSell?: string;
  qtaAltSell?: number;
  priceAltSell?: number;
  autocadFile?: string;
  brand?: string;
  webVisible?: string;
  active?: string;
  company?: string;
  srcs?: string;
  createAt?: Date | string;
  updateAt?: Date | string;
}

export interface ProductSeoTag {
  skuProduct: string;
  seoText: string;
  seoMetaTitle: string;
  seoMetaDescription: string;
  language: string;
  titleH1?: string;
  seoTextUnderH1?: string;
  seoTextOverTechnicalData?: string;
  seoTextUnderTechnicalData?: string;
}

export interface Product extends BaseProduct {
  assets?: ProductAsset[];
  category?: string;
  categories?: ProductCategory[];
  sectors?: ProductSector[];
  descriptions?: ProductDescription[];
  children?: BaseProduct[];
  images?: string[];
  cover?: string;
  technicalData?: ProductTechnicalData[];
  categoriesCalculated?: number[];
  allCategoriesCalculated?: number[];
  categoriesCalculatedInfo?: Category[][];
  link?: string;
  otherProductsLink?: string;
  seoTags?: ProductSeoTag[];
}

export interface ProductAsset {
  id?: number;
  skuProduct: string;
  type?: string;
  language?: string;
  value?: string;
  orderPosition?: number;
  createAt?: Date | string;
  updateAt?: Date | string;
}

export interface ProductCategory {
  skuProduct: string;
  idCategory: number;
  createAt?: Date | string;
  updateAt?: Date | string;
}

export interface ProductSector {
  skuProduct: string;
  idSector: number;
  createAt?: Date | string;
  updateAt?: Date | string;
}

export interface ProductDescription {
  skuProduct: string;
  language: string;
  description: string;
  brand?: string;
  createAt?: Date | string;
  updateAt?: Date | string;
}

export interface ProductChildCart extends BaseProduct {
  file?: string;
}

export interface ProductClientDescription {
  skuProduct: string;
  idSap: string;
  description?: string;
  createAt?: Date | string;
  updateAt?: Date | string;
}

export interface ProductImage {
  id?: number;
  skuProduct: string;
  language?: string;
  type?: string;
  ce?: string;
  file?: string;
  description?: string;
  orderPosition?: number;
  brand?: string;
  createAt?: Date | string;
  updateAt?: Date | string;
}

export interface ProductQuantities {
  sku: string;
  quantity: number;
}

export interface ProductTechnicalData {
  id?: number;
  idModel: number;
  skuProduct: string;
  language: string;
  row: number;
  name?: string;
  value?: string;
  createAt?: Date | string;
  updateAt?: Date | string;
}

export interface ProductAssetsBody {
  fileType: ProductAssetsType;
  sku: string;
  lang: string;
}

export interface ResultSearch {
  item: {
    id: string;
    type: string;
    name: string;
    children: string[];
    tags: string[];
    technical_data: string[];
  };
  refIndex: number;
  score?: number;
}

export interface Category {
  id: number;
  idFather: number;
  order: number;
  depth: number;
  name?: string;
  nameIt?: string | null;
  nameEn?: string | null;
  nameEs?: string | null;
  nameFr?: string | null;
  nameDe?: string | null;
  nameRu?: string | null;
  nameCn?: string | null;
  nameZh?: string | null;
  namePr?: string | null;
  nameNl?: string | null;
  lft: number;
  rgt: number;
  createAt?: Date;
  updateAt?: Date;
  mainCategory?: boolean;
  [key: string]: any;
}

export type CatalogCategory = Omit<Category, 'lft' | 'rgt'> & {
  depth: number;
};

export interface Sector {
  code: number;
  name: string;
}

export interface SectorWithCategoriesCount extends Sector {
  categories: number[];
  count: number;
}

export interface NewsType {
  data?: {
    id: number;
    attributes: {
      type: string;
    };
  };
}

export interface LocationSchema {
  postalCode?: string;
  addressCountry?: string;
  addressLocality?: string;
  streetAddress?: string;
  addressRegion?: string;
}

export interface RepeatableStrapiImage {
  data?: StrapiImage[];
}

export interface SingleStrapiImage {
  data?: StrapiImage;
}

export interface StrapiImage {
  id: string | number;
  attributes?: {
    height: string;
    width: string;
    caption: string;
    alternativeText: string;
    name: string;
    formats: {
      thumbnail?: {
        height: number | null;
        width: number | null;
        size: number | null;
        path: string | number | null;
        name: string | null;
        mime: string | null;
        hash: string | null;
        url: string | null;
        ext: string | null;
      };
    };
    hash: string | null;
    ext: string | null;
    mime: string | null;
    size: number | null;
    url: string | null;
    previewUrl: string | null;
    provider: string | null;
    provider_metadata: string | null;
    createdAt: Date;
    updatedAt: Date;
  };
}

export interface Link {
  url: string;
  text: string;
  idDwh?: string;
}

export enum LinkType {
  primary = 'primary',
  secondary = 'secondary',
}

export interface MegamenuLink {
  // [key: string]: any;
  // For any future situations or for any situations which we don't know the type because it is coming from STRAPI
  // By the way keep model updated!

  id: number;
  idDwh?: string;
  text?: string;
  coverTitle?: string;
  coverDescription?: string;
  recommendedProductIdDwh?: string;
  recommendedProductDescription?: string;
  url?: string;
  coverImage?: SingleStrapiImage;
  firstChild?: Category[];
  recommendedProduct?: Product;
  slug: string;
  hideOnMobile: boolean;
  shortText: string;

  [key: string]: any;
}

export interface SectionVoice {
  id?: number;
  label: string;
  link: string;
  active: boolean;
}

export interface Section {
  title: string;
  voices: SectionVoice[];
}

export interface GlobalResponseStrapi {
  // [key: string]: any;
  // For any future situations or for any situations which we don't know the type because it is coming from STRAPI
  // By the way keep model updated!

  data: {
    attributes: {
      locale: string;
      createdAt: Date;
      updatedAt: Date;
      header: {
        id: string | number;
        logo: SingleStrapiImage;
        mainLinks: Link[];
        megamenuLinks: MegamenuLink[];
        reservedArea: ReservedArea;
        [key: string]: any;
      };
      footer: {
        [key: string]: any;
      };
      favicon: {
        [key: string]: any;
      };
      metadata: {
        [key: string]: any;
      };
      fixedPageUrls: {
        [key: string]: any;
      };
      localizations: {
        data: {
          attributes: {
            [key: string]: any;
            fixedPageUrls: {
              [key: string]: any;
            };
          };
        }[];
      };
      newsAndEventsMerged?: boolean;
    };
    meta: {
      [key: string]: any;
    };
    [key: string]: any;
  };
  [key: string]: any;
}

export interface GlobalResponse {
  // [key: string]: any;
  // For any future situations or for any situations which we don't know the type because it is coming from STRAPI
  // By the way keep model updated!

  attributes: {
    locale: string;
    createdAt: Date;
    updatedAt: Date;
    header: {
      id: string | number;
      logo: SingleStrapiImage;
      mainLinks: Link[];
      megamenuLinks: MegamenuLink[];
      reservedArea: ReservedArea;
      [key: string]: any;
    };
    footer: {
      [key: string]: any;
    };
    favicon: {
      [key: string]: any;
    };
    metadata: {
      [key: string]: any;
    };
    localizations: {
      data: {
        attributes: {
          [key: string]: any;
          fixedPageUrls: {
            [key: string]: any;
          };
        };
      }[];
    };
    fixedPageUrls: {
      [key: string]: any;
    };
    newsAndEventsMerged?: boolean;
    hideBannerInSectorPages?: boolean;
    hideBannerInCategoryPages?: boolean;
    prodsAltLocGoToHome?: boolean;
    catsAltLocGoToHome?: boolean;
  };
  meta: {
    [key: string]: any;
  };
  [key: string]: any;
}

export interface Slug {
  slug: string | string[];
  type: Pages;
  eventSlug?: string;
  workshopSlug?: string;
  uri: string;
  supportInfoCache?: {
    [key: string]: any;
  };
  locale: string;
  query: any;
  megamenuLinks: any;
}

export interface SliderConfigs {
  autoPlay: boolean;
  autoPlayInterval: number;
  animationDuration: number;
  animationType: 'fadeout' | 'slide';
  infinite: boolean;
  showCarouselTitle?: boolean;
  numberOfElementsSM?: number;
  numberOfElementsXL?: number;
  numberOfElementsLG?: number;
  numberOfElementsMD?: number;
  numberOfElementsXS?: number;
  forceHideGoToArchive?: boolean;
  forceHideDate?: boolean;
  alternativeLink?: string;
  alternativeLinkText?: string;
}

export interface WorkWithUsFormikValues {
  email?: string;
  firstname?: string;
  lastname?: string;
  address?: string;
  zip?: string;
  location?: string;
  province?: string;
  country?: string;
  mobilephone?: string;
  qualification?: string;
  position?: string;
  notes?: string;
  fileCV?: any;
}

export interface SupportInfo {
  newsAndEventsMerged: boolean;
}

export interface CategoryJsonLd {
  category?: string;
  title: string;
  description: string;
  image: string;
  mobileImage: string;
}

export interface StrapiComponentProps {
  __component: string;
  [key: string]: any;
}
